import type { InkColor, LaserTech, SkinTone, TattooAge } from '@/lib/calculators'
import {
  calculateLaserRemoval,
  INK_COLOR_DIFFICULTY,
  LASER_TECH,
  SKIN_TONE_MULTIPLIERS,
  TATTOO_AGE_SESSIONS,
} from '@/lib/calculators'
import SaveEstimateButton from '@/components/SaveEstimateButton'
import EmailEstimateButton from '@/components/EmailEstimateButton'
import {AlertTriangle, Zap} from 'lucide-react'
import { useMemo, useState } from 'react'

function LaserCalculator() {
  const [sqIn, setSqIn] = useState(12)
  const [inkColor, setInkColor] = useState<InkColor>('black')
  const [tattooAge, setTattooAge] = useState<TattooAge>('oneToFive')
  const [skinTone, setSkinTone] = useState<SkinTone>('typeI_II')
  const [tech, setTech] = useState<LaserTech>('nanosecond')

  const result = useMemo(
    () => calculateLaserRemoval({ squareInches: sqIn, inkColor, tattooAge, skinTone, tech }),
    [sqIn, inkColor, tattooAge, skinTone, tech],
  )

  return (
    <section id="laser-calculator" className="bg-[#0a0908] px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">
            Laser Tattoo Removal Cost Calculator
          </h2>
          <p className="mt-3 text-slate-400">
            Estimate laser tattoo removal cost per session and total sessions needed &mdash; the real cost for
            treating tattoos with modern picosecond and Q-switched lasers.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-5">
          {/* Inputs */}
          <div className="space-y-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 lg:col-span-3 lg:p-8">
            <div>
              <label htmlFor="laser-size" className="mb-2 block text-sm font-medium text-slate-300">
                Tattoo Size (square inches)
              </label>
              <input
                id="laser-size"
                type="number"
                min={1}
                step={1}
                value={sqIn}
                onChange={e => setSqIn(Number.parseFloat(e.target.value) || 0)}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-lg text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
              />
            </div>

            <div>
              <label htmlFor="ink-color" className="mb-2 block text-sm font-medium text-slate-300">Ink Color</label>
              <select
                id="ink-color"
                value={inkColor}
                onChange={e => setInkColor(e.target.value as InkColor)}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
              >
                {Object.entries(INK_COLOR_DIFFICULTY).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="tattoo-age" className="mb-2 block text-sm font-medium text-slate-300">Tattoo Age</label>
              <select
                id="tattoo-age"
                value={tattooAge}
                onChange={e => setTattooAge(e.target.value as TattooAge)}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
              >
                {Object.entries(TATTOO_AGE_SESSIONS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="skin-tone" className="mb-2 block text-sm font-medium text-slate-300">
                Fitzpatrick Skin Tone Scale
              </label>
              <select
                id="skin-tone"
                value={skinTone}
                onChange={e => setSkinTone(e.target.value as SkinTone)}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
              >
                {Object.entries(SKIN_TONE_MULTIPLIERS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
              {result.warning && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span>{result.warning}</span>
                </div>
              )}
            </div>

            <div>
              <span className="mb-2 block text-sm font-medium text-slate-300">Laser Technology</span>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(LASER_TECH).map(([key, val]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTech(key as LaserTech)}
                    className={`rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                      tech === key
                        ? 'border-amber-400 bg-amber-500/10 text-amber-200'
                        : 'border-white/10 bg-black/30 text-slate-300 hover:border-white/20'
                    }`}
                  >
                    <span className="flex items-center gap-1.5 font-medium">
                      <Zap className="h-3.5 w-3.5" aria-hidden="true" />
                      {val.label}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">
                      $
                      {val.baseRatePerSqIn}
                      /sq.in base
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Output */}
          <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-b from-amber-500/[0.08] to-transparent p-6 lg:col-span-2 lg:p-8">
            <h3 className="font-serif text-xl font-semibold text-white">Removal Project Estimate</h3>
            <p className="mt-1 text-sm text-slate-400">Estimated total sessions and full project cost</p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-black/30 p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-amber-300">Per Session</p>
                <p className="mt-1 font-serif text-2xl font-bold text-amber-400">
                  $
                  {result.costPerSession.toLocaleString()}
                </p>
              </div>
              <div className="rounded-xl bg-black/30 p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-amber-300">Est. Sessions</p>
                <p className="mt-1 font-serif text-2xl font-bold text-amber-400">{result.totalSessions}</p>
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-black/30 p-6 text-center">
              <p className="text-xs uppercase tracking-widest text-amber-300">Total Removal Cost</p>
              <p className="mt-2 font-serif text-4xl font-bold text-amber-400">
                $
                {result.totalCost.toLocaleString()}
              </p>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              Estimates based on standard laser tattoo removal industry pricing. Actual laser tattoo removal cost per
              session varies by clinic, tattoo density, and skin response. Consult a licensed provider for a full
              consultation.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <SaveEstimateButton
                calculatorType="laser"
                summary={`${sqIn} sq. in., ${LASER_TECH[tech].label} \u2014 ${result.totalSessions} sessions, total $${result.totalCost.toLocaleString()}`}
                inputs={{ sqIn, inkColor, tattooAge, skinTone, tech }}
                results={result}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-5 py-3 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60"
              />
              <EmailEstimateButton
                calculatorType="laser"
                summary={`${sqIn} sq. in., ${LASER_TECH[tech].label} \u2014 ${result.totalSessions} sessions, total $${result.totalCost.toLocaleString()}`}
                inputs={{ sqIn, inkColor, tattooAge, skinTone, tech }}
                results={result}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default LaserCalculator
